import React, {
	Dispatch,
	FC,
	ReactNode,
	SetStateAction,
	SyntheticEvent,
	useContext,
} from "react";

import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import {
	mobilePillTabsSx,
	sharedBreadcrumbsSx,
	sharedTabsSx,
} from "@/styles/sharedNavStyles";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import { useMediaQuery, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";


interface NavBarProps {
	tabs: NavBarTab[];
	links: ReactNode[];
	/** Optional custom breadcrumbs (overrides links when provided) */
	breadcrumbs?: ReactNode;
	currentTab: number;
	setCurrentTab: Dispatch<SetStateAction<number>>;
}

export interface NavBarTab {
	name: string;
	index: number;
	hasIcon: boolean;
	translationKey?: string;
	externalUrl?: string;
}

export const NavBar: FC<NavBarProps> = ({
	tabs,
	links,
	breadcrumbs,
	currentTab: value,
	setCurrentTab: setValue,
}: NavBarProps) => {
	const translation: Language | null = useContext(TranslationContext);
	const theme = useTheme();
	const isMobile = useMediaQuery("(max-width:767px)");

	function a11yProps(index: number) {
		return {
			id: `simple-tab-${index}`,
			"aria-controls": `simple-tabpanel-${index}`,
		};
	}

	const handleChange = (event: SyntheticEvent, newValue: number) => {
		const externalTab = tabs[newValue]?.externalUrl;
		if (externalTab) {
			window.open(externalTab, "_self"); // Open the settings page in a new tab
		} else {
			setValue(newValue); // Update the local state to change the displayed tab content
		}
	};

	return (
		<Box
			sx={{
				width: "100%",
				minWidth: 0,
				pl: { xs: "env(safe-area-inset-left)", sm: 0 },
			}}
		>
			<Box sx={{ width: "100%", minWidth: 0 }}>
				<Tabs
					variant="scrollable"
					scrollButtons="auto"
					allowScrollButtonsMobile
					value={value}
					textColor="inherit"
					indicatorColor="primary"
					sx={isMobile ? mobilePillTabsSx(theme) : sharedTabsSx}
					onChange={handleChange}
					aria-label={GetLanguageValueAsString(translation, "Auto.Shared.Ui.NavBar.L83C17.Navigation", "Navigation")}
				>
					{tabs.map((el, index) => {
						return (
							<Tab
								key={index}
								label={
									<Translate String={el.translationKey ?? el.name}>
										{el.name}
									</Translate>
								}
								{...a11yProps(el.index)}
								icon={el.hasIcon ? <ArrowOutwardIcon fontSize="small" /> : ""}
								iconPosition="end"
							/>
						);
					})}
				</Tabs>
			</Box>
			{(breadcrumbs || links.length > 0) && (
				<Box sx={sharedBreadcrumbsSx} role="presentation">
					{breadcrumbs ?? (
						<Breadcrumbs aria-label="breadcrumb">{links}</Breadcrumbs>
					)}
				</Box>
			)}
		</Box>
	);
};
